import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@clerk/react'
import { apiFetch } from '../../services/api'
import { useIsAdmin, useFeatureFlag } from '../../hooks/useFeatureFlags'

interface PullLimits {
  used: number
  limit: number
  remaining: number
  isUnlimited: boolean
}

export function usePullLimits() {
  const { isSignedIn } = useAuth()
  const [limits, setLimits] = useState<PullLimits>({ used: 0, limit: 1, remaining: 1, isUnlimited: false })
  // Until the fetch resolves, `limits` is a guess (limit 1) — callers that
  // display numbers should wait for `loaded`, or users see a wrong-count
  // flash. `loaded` only ever becomes true on a REAL payload — it must stay
  // false on failure so PullLimitBadge keeps hiding rather than rendering
  // the optimistic guess as if it were the truth.
  const [loaded, setLoaded] = useState(false)
  // `settled` becomes true once the fetch attempt has FINISHED, success or
  // failure, OR once we've determined there is nothing to fetch (signed out)
  // — unlike `loaded`, it does not imply the data is real. Callers that only
  // need to know "is it safe to act now" (e.g. ReadingPage's draw gate)
  // should wait on this instead of `loaded`: a failed limits fetch must fail
  // OPEN (let the action proceed against the optimistic default; the server
  // remains the real enforcer and 429s regardless) rather than waiting
  // forever for a `loaded` that a broken endpoint will never send. It must
  // also settle when signed out, so a caller waiting on it never hangs
  // forever just because there was never a request to finish.
  const [settled, setSettled] = useState(false)

  const refresh = () => {
    apiFetch('/readings/limits')
      .then(r => r.json())
      .then((data) => {
        setLimits(data)
        setLoaded(true)
      })
      .catch(() => {})
      .finally(() => setSettled(true))
  }

  useEffect(() => {
    if (isSignedIn) refresh()
    else setSettled(true)
  }, [isSignedIn])

  return { ...limits, loaded, settled, refresh }
}

export function PullLimitBadge() {
  const { used, limit, remaining, isUnlimited, loaded } = usePullLimits()
  const isAdmin = useIsAdmin()
  const paymentsEnabled = useFeatureFlag('payments_enabled')
  const navigate = useNavigate()

  if (!loaded) return null

  if (isUnlimited) {
    return (
      <div className="text-sm text-text-secondary font-body">
        {isAdmin ? '🃏 Unlimited (Admin)' : '🃏 Unlimited pulls'}
      </div>
    )
  }

  const atLimit = remaining <= 0
  const lowPulls = remaining === 1

  return (
    <div className="space-y-1.5">
      <div className={`text-sm font-body font-medium ${
        atLimit ? 'text-rose-light' : lowPulls ? 'text-gold' : 'text-text-secondary'
      }`}>
        🃏 {remaining} of {limit} daily pull{limit === 1 ? '' : 's'} left
      </div>
      {atLimit && (
        <div className="bg-rose/10 rounded-lg p-3 border border-rose/20">
          <p className="text-sm text-text-primary/80 font-body">
            {limit === 1
              ? "You've used your free pull for today."
              : `You've used all ${limit} of your free pulls for today.`}
          </p>
          {paymentsEnabled ? (
            <button
              onClick={() => navigate('/upgrade?from=pull-limit')}
              className="mt-2 text-sm text-gold font-medium hover:text-gold-light transition-colors"
            >
              Upgrade to Cosmic for unlimited readings →
            </button>
          ) : (
            <p className="mt-2 text-xs text-text-muted/70 italic font-display leading-relaxed">
              Cosmic checkout is paused during beta. Pulls reset at midnight.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
